import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '../../utils';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'danger' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          // Base styles
          'inline-flex items-center justify-center rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-retri-blue focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none',
          
          // Variant styles
          {
            'bg-gray-900 text-gray-50 hover:bg-gray-900/90': variant === 'default',
            'bg-retri-blue text-white hover:bg-retri-bluedark font-semibold': variant === 'primary',
            'bg-gray-200 text-gray-800 font-semibold hover:bg-gray-300': variant === 'secondary',
            'bg-retri-green text-white hover:bg-green-600': variant === 'success',
            'bg-retri-red text-white hover:bg-red-600': variant === 'danger',
            'border-2 border-retri-blue bg-white text-retri-blue font-semibold hover:bg-retri-blue hover:text-white': variant === 'outline',
            'hover:bg-gray-200 hover:text-gray-900 text-gray-700': variant === 'ghost',
          },
          
          // Size styles
          {
            'h-11 px-4 py-2': size === 'default',
            'h-9 rounded-md px-3': size === 'sm',
            'h-11 rounded-md px-8': size === 'lg',
            'h-10 w-10': size === 'icon',
          },
          
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';

export default Button;
